import { createStore } from "vuex";
import VuexPersistence from "vuex-persist";
import { Network } from "@capacitor/network";
import { App } from "@capacitor/app";
import { syncApi } from "@/services/index";
import updateService from "@/services/updateService";
import { db } from "@/services/db.js";

export default createStore({
    state: {
        syncing: false,
        queue: false,
        setup: false,
        inspection_sync_progress: 0,
        queue_progress: 0,
        speeds: {
            upload: 0,
            download: 0,
        },
        upload_speed: null,
        temp_save: null,
        image_timestamp: false,
        image_coordinates: false,
        image_save_in_gallery: false,
        image_save_local_backup: true,
        image_set_inspector_name: false,
        flash: true,
        measurment_type_opened: null,
        orientation: "portrait",
        inspection_method_code: "IB22",
        strokeColor: "red",
        save_image_locally: false,
        torch: false,
        server_status: "online",
        inspector: {},
        location_item: {},
        active_inspection: {},
        location_id: null,
        online: true,
        dark_mode: false,
        user: null,
        loading: false,
        refresh: false,
        location_item_states: [],
        location_item_component_states: [],
        inspection_id: null,
        location_item_uuid: null,
        location_item_component_uuid: null,
        tree_filters: {
            stats: true,
        },
        default_measurement_instruments: [],
        remarks_opened: "standard",
        inspection_polling: false,
        selected_location_item_uuid: null,
        checklist_answers: [],
        list_loading: false,
        action_package: {
            type: "inspection",
            inspection_id: null,
            actionable_uuid: null,
        },
        notification_ids: [],
        active_menu: "notifications",
        actions_filter: "OPEN",
        active_address: null,
        drag_mode: false,
        location_uuid: null,
        inspection_uuid: null,
        scroll_height: 0,
        component_count_key: 0,
        last_searched_items: [],
        selected_location_item_component_uuids: [],
        selection_mode: false,
        inspection_filters: {}, // Store filters by inspection ID
        show_sidebar: false,
        last_sync_inspections: null,
        tab_inspection: "location_items",
        tab_component: "measurements",
        update_modal: false,
        updateInfo: null, // Stores info about available updates
        updateModalOpen: false, // Shared flag to prevent duplicate update modals
        connectionType: "wifi",
        sync_wifi_only: false,
        api_url: null,
        bearer: null,
        tours: true,
        tours_completed: [],
        global_settings: [],
        is_active: true,
        event_notification: null,
        echo: null,
        inspections_synced: [],
        line_thickness: 5,
        show_instruments: "own",
        force_offline: false,
        appActive: true, // Track if app is in foreground or background (for iOS camera)
    },
    mutations: {
        setForceOffline(state, force_offline) {
            state.force_offline = force_offline;
        },
        setInspectionSyncProgress(state, progress) {
            state.inspection_sync_progress = progress;
        },
        setQueueProgress(state, progress) {
            state.queue_progress = progress;
        },
        setSetup(state, setup) {
            state.setup = setup;
        },
        setSyncing(state, syncing) {
            state.syncing = syncing;
        },
        setQueue(state, running) {
            state.queue = running;
        },
        setSpeeds(state, speeds) {
            state.speeds = speeds;
        },
        setTempSave(state, temp_save) {
            state.temp_save = temp_save;
        },
        setImageTimestamp(state, image_timestamp) {
            state.image_timestamp = image_timestamp;
        },
        setImageCoordinates(state, image_coordinates) {
            state.image_coordinates = image_coordinates;
        },
        setImageSaveInGallery(state, image_save_in_gallery) {
            state.image_save_in_gallery = image_save_in_gallery;
        },
        setImageSaveLocalBackup(state, image_save_local_backup) {
            state.image_save_local_backup = image_save_local_backup;
        },
        setImageInspectorName(state, val) {
            state.image_set_inspector_name = val;
        },
        setFlash(state, flash) {
            state.flash = flash;
        },
        setMeasurmentTypeOpened(state, measurment_type_opened) {
            state.measurment_type_opened = measurment_type_opened;
        },
        setOrientation(state, orientation) {
            state.orientation = orientation;
        },
        setInspectionMethodCode(state, code) {
            state.inspection_method_code = code;
        },
        setShowInstruments(state, show_instruments) {
            state.show_instruments = show_instruments;
        },
        setStrokeColor(state, color) {
            state.strokeColor = color;
        },
        setTorch(state, torch) {
            state.torch = torch;
        },
        setSaveImageLocally(state, save_image_locally) {
            state.save_image_locally = save_image_locally;
        },
        setLineThickness(state, thickness) {
            state.line_thickness = thickness;
        },
        setServerStatus(state, status) {
            state.server_status = status;
        },
        setInspectionSynced(state, inspection) {
            let inspection_synced = state.inspections_synced.find(
                (item) => item.inspection_uuid == inspection.inspection_uuid
            );
            if (inspection_synced) {
                inspection_synced.updated_at = inspection.updated_at;
            } else {
                state.inspections_synced.push(inspection);
            }
        },
        setEcho(state, echo) {
            state.echo = echo;
        },
        setEventNotification(state, event_notification) {
            state.event_notification = event_notification;
        },
        setGlobalSettings(state, global_settings) {
            state.global_settings = global_settings;
        },
        setTours(state, tours) {
            state.tours = tours;
        },
        setTourCompleted(state, new_tour_completed) {
            // if the tour is not already in the list, add it
            if (!state.tours_completed.includes(new_tour_completed)) {
                state.tours_completed.push(new_tour_completed);
            }
        },
        setToursCompleted(state, tours_completed) {
            state.tours_completed = tours_completed;
        },
        setApiUrl(state, api_url) {
            state.api_url = api_url;
        },
        setBearer(state, bearer) {
            state.bearer = bearer;
        },
        setConnectionType(state, connectionType) {
            state.connectionType = connectionType;
        },
        setAppIsActive(state, isActive) {
            state.is_active = isActive;
        },
        setComponentCounts(state, component_counts) {
            state.component_counts = component_counts;
        },
        setLocationItem(state, location_item) {
            state.location_item = location_item;
        },
        setOnline(state, online) {
            state.online = online;
        },
        setDarkMode(state, dark_mode) {
            document.body.classList.toggle("dark", dark_mode);
            state.dark_mode = dark_mode;
        },
        setUser(state, user) {
            state.user = user;
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
        setLocationItemState(state, payload) {
            let location_item_state = state.location_item_states.find((lc) => lc.uuid == payload.uuid);

            if (!location_item_state) {
                state.location_item_states.push(payload);
            } else {
                location_item_state = payload;
            }
        },
        setLocationItemComponentState(state, payload) {
            let location_item_component_state = state.location_item_component_states.find(
                (lc) => lc.uuid == payload.uuid
            );

            if (!location_item_component_state) {
                state.location_item_component_states.push(payload);
            } else {
                location_item_component_state = payload;
            }
        },
        setInspectionId(state, inspection_id) {
            state.inspection_id = inspection_id;
        },
        setLocationItemUuid(state, location_item_uuid) {
            state.location_item_uuid = location_item_uuid;
        },
        setLocationItemComponentUuid(state, location_item_component_uuid) {
            state.location_item_component_uuid = location_item_component_uuid;
        },
        setTreeFilters(state, tree_filters) {
            state.tree_filters = tree_filters;
        },
        setDefaultMeasurementInstruments(state, default_measurement_instruments) {
            state.default_measurement_instruments = default_measurement_instruments;
        },
        setRemarksOpened(state, remarks_opened) {
            state.remarks_opened = remarks_opened;
        },
        setSelectedLocationItem(state, uuid) {
            state.selected_location_item_uuid = uuid;
        },
        setInspectionPolling(state, inspection_polling) {
            state.inspection_polling = inspection_polling;
        },
        setChecklistAnswers(state, checklist_answers) {
            state.checklist_answers = checklist_answers;
        },
        setListLoading(state, list_loading) {
            state.list_loading = list_loading;
        },
        setLocationId(state, location_id) {
            state.location_id = location_id;
        },
        setActionPackage(state, action_package) {
            state.action_package = action_package;
        },
        setNotificationIds(state, notification_ids) {
            state.notification_ids = notification_ids;
        },
        setActiveMenu(state, active_menu) {
            state.active_menu = active_menu;
        },
        setActionsFilter(state, actions_filter) {
            state.actions_filter = actions_filter;
        },
        setActiveAddress(state, active_address) {
            state.active_address = active_address;
        },
        setDragMode(state, drag_mode) {
            state.drag_mode = drag_mode;
        },
        setLocationUuid(state, location_uuid) {
            state.location_uuid = location_uuid;
        },
        setInspectionUuid(state, inspection_uuid) {
            state.inspection_uuid = inspection_uuid;
        },
        setScrollHeight(state, scroll_height) {
            state.scroll_height = scroll_height;
        },
        setComponentCountKey(state, component_count_key) {
            state.component_count_key = component_count_key;
        },
        setLastSearchedItems(state, last_searched_items) {
            state.last_searched_items = last_searched_items;
        },
        setSelectedLocationItemComponentUuids(state, selected_location_item_component_uuids) {
            state.selected_location_item_component_uuids = selected_location_item_component_uuids;
        },
        setSelectionMode(state, selection_mode) {
            state.selection_mode = selection_mode;
        },
        setInspectionFilters(state, { inspection_id, filters }) {
            state.inspection_filters = {
                ...state.inspection_filters,
                [inspection_id]: filters
            };
        },
        setShowSidebar(state, show_sidebar) {
            state.show_sidebar = show_sidebar;
        },
        setLastSyncInspections(state, last_sync_inspections) {
            state.last_sync_inspections = last_sync_inspections;
        },
        setInspectionTab(state, tab_inspection) {
            state.tab_inspection = tab_inspection;
        },
        setComponentTab(state, tab_component) {
            state.tab_component = tab_component;
        },
        setUpdateModal(state, update_modal) {
            state.update_modal = update_modal;
        },
        setUpdateInfo(state, updateInfo) {
            state.updateInfo = updateInfo;
        },
        setUpdateModalOpen(state, isOpen) {
            state.updateModalOpen = isOpen;
        },
        setAppActive(state, isActive) {
            state.appActive = isActive;
        },
        setInspections(state, inspections) {
            state.inspections = inspections;
        },
        setActions(state, actions) {
            state.actions = actions;
        },
    },
    actions: {
        setSetup({ commit }, setup) {
            commit("setSetup", setup);
        },
        setForceOffline({ commit, state }, force_offline) {
            // Keep track of the change
            const wasForceOffline = state.force_offline;

            // Update the state
            commit("setForceOffline", force_offline);

            // If turning off force offline and we have network connection,
            // force an online state update to trigger processing
            if (wasForceOffline && !force_offline) {
                // Get actual network status and update online state
                Network.getStatus().then(async (status) => {
                    if (status.connected) {
                        console.log("Force offline turned off with active network, updating online state");
                        commit("setOnline", true);
                        commit("setConnectionType", status.connectionType);
                        
                        // Force process queue including images
                        console.log("Triggering sync after force offline disabled");
                        
                        // Import syncApi dynamically to avoid circular dependency
                        const { syncApi } = await import("@/services/index");
                        
                        // Check for pending images specifically
                        const { db } = await import("@/services/db");
                        const pendingImages = await db.images
                            .where('upload_status')
                            .anyOf(['pending', 'failed', 'retrying'])
                            .count();
                        
                        if (pendingImages > 0) {
                            console.log(`Found ${pendingImages} pending images, triggering upload`);
                        }
                        
                        // Process queue with force sync to ensure images are included
                        setTimeout(() => {
                            syncApi.processQueueWithParams({
                                ignoreOffline: true,
                                forceSync: true
                            }).catch(err => {
                                console.error("Error processing queue after force offline disabled:", err);
                            });
                        }, 500);
                    }
                });
            }
        },
        setConnectionType({ commit }, connectionType) {
            commit("setConnectionType", connectionType);
        },
        setInspectionSyncProgress({ commit }, progress) {
            commit("setInspectionSyncProgress", progress);
        },
        setQueueProgress({ commit }, progress) {
            commit("setQueueProgress", progress);
        },
        setSyncing({ commit }, syncing) {
            commit("setSyncing", syncing);
        },
        setQueue({ commit }, running) {
            commit("setQueue", running);
        },
        setSpeeds({ commit }, speeds) {
            commit("setSpeeds", speeds);
        },
        setTempSave({ commit }, temp_save) {
            commit("setTempSave", temp_save);
        },
        setImageTimestamp({ commit }, image_timestamp) {
            commit("setImageTimestamp", image_timestamp);
        },
        setImageCoordinates({ commit }, image_coordinates) {
            commit("setImageCoordinates", image_coordinates);
        },
        setImageSaveInGallery({ commit }, image_save_in_gallery) {
            commit("setImageSaveInGallery", image_save_in_gallery);
        },
        setImageSaveLocalBackup({ commit }, image_save_local_backup) {
            commit("setImageSaveLocalBackup", image_save_local_backup);
        },
        setImageInspectorName({ commit }, val) {
            commit("setImageInspectorName", val);
        },
        setFlash({ commit }, flash) {
            commit("setFlash", flash);
        },
        setMeasurmentTypeOpened({ commit }, id) {
            commit("setMeasurmentTypeOpened", id);
        },
        setOrientation({ commit }, orientation) {
            commit("setOrientation", orientation);
        },
        setInspectionMethodCode({ commit }, code) {
            commit("setInspectionMethodCode", code);
        },
        setShowInstruments({ commit }, show_instruments) {
            commit("setShowInstruments", show_instruments);
        },
        setTorch({ commit }, torch) {
            commit("setTorch", torch);
        },
        setSaveImageLocally({ commit }, save_image_locally) {
            commit("setSaveImageLocally", save_image_locally);
        },
        setLineThickness({ commit }, thickness) {
            commit("setLineThickness", thickness);
        },
        setServerStatus({ commit }, status) {
            console.log("Setting server status to", status);
            commit("setServerStatus", status);
        },
        setInspectionSynced({ commit }, inspection) {
            commit("setInspectionSynced", inspection);
        },
        setEcho({ commit }, echo) {
            commit("setEcho", echo);
        },
        setEventNotification({ commit }, event_notification) {
            commit("setEventNotification", event_notification);
        },
        setGlobalSettings({ commit }, global_settings) {
            commit("setGlobalSettings", global_settings);
        },
        setTours({ commit }, Tours) {
            commit("setTours", Tours);
        },
        setTourCompleted({ commit }, new_tour_completed) {
            commit("setTourCompleted", new_tour_completed);
        },
        setToursCompleted({ commit }, tours_completed) {
            commit("setToursCompleted", tours_completed);
        },
        setBearer({ commit }, bearer) {
            commit("setBearer", bearer);
        },
        setApiUrl({ commit }, api_url) {
            commit("setApiUrl", api_url);
        },
        setComponentCounts({ commit }, component_counts) {
            commit("setComponentCounts", component_counts);
        },
        setLocationItem({ commit }, payload) {
            commit("setLocationItem", payload);
        },
        setOnline({ commit }, online) {
            commit("setOnline", online);
        },
        setDarkMode({ commit }, dark_mode) {
            commit("setDarkMode", dark_mode);
        },
        setUser({ commit }, user) {
            commit("setUser", user);
        },
        setLoading({ commit }, loading) {
            commit("setLoading", loading);
        },
        setLocationItemState({ commit }, payload) {
            commit("setLocationItemState", payload);
        },
        setLocationItemComponentState({ commit }, payload) {
            commit("setLocationItemComponentState", payload);
        },
        setInspectionId({ commit }, inspection_id) {
            commit("setInspectionId", inspection_id);
        },
        setLocationItemUuid({ commit }, location_item_uuid) {
            commit("setLocationItemUuid", location_item_uuid);
        },
        setLocationItemComponentUuid({ commit }, location_item_component_uuid) {
            commit("setLocationItemComponentUuid", location_item_component_uuid);
        },
        setTreeFilters({ commit }, tree_filters) {
            commit("setTreeFilters", tree_filters);
        },
        setDefaultMeasurementInstruments({ commit }, default_measurement_instruments) {
            commit("setDefaultMeasurementInstruments", default_measurement_instruments);
        },
        setRemarksOpened({ commit }, remarks_opened) {
            commit("setRemarksOpened", remarks_opened);
        },
        setSelectedLocationItem({ commit }, selected_location_item) {
            commit("setSelectedLocationItem", selected_location_item);
        },
        setInspectionPolling({ commit }, inspection_polling) {
            commit("setInspectionPolling", inspection_polling);
        },
        setChecklistAnswers({ commit }, checklist_answers) {
            commit("setChecklistAnswers", checklist_answers);
        },
        setListLoading({ commit }, list_loading) {
            commit("setListLoading", list_loading);
        },
        setLocationId({ commit }, location_id) {
            commit("setLocationId", location_id);
        },
        setActionPackage({ commit }, action_package) {
            commit("setActionPackage", action_package);
        },
        setNotificationIds({ commit }, notification_ids) {
            commit("setNotificationIds", notification_ids);
        },
        setActiveMenu({ commit }, active_menu) {
            commit("setActiveMenu", active_menu);
        },
        setActionsFilter({ commit }, actions_filter) {
            commit("setActionsFilter", actions_filter);
        },
        setActiveAddress({ commit }, active_address) {
            commit("setActiveAddress", active_address);
        },
        setDragMode({ commit }, drag_mode) {
            commit("setDragMode", drag_mode);
        },
        setLocationUuid({ commit }, location_uuid) {
            commit("setLocationUuid", location_uuid);
        },
        setInspectionUuid({ commit }, inspection_uuid) {
            commit("setInspectionUuid", inspection_uuid);
        },
        setScrollHeight({ commit }, scroll_height) {
            commit("setScrollHeight", scroll_height);
        },
        setComponentCountKey({ commit, state }) {
            // Increment counter instead of using Math.random() to prevent full component tree destruction
            // This still forces a re-render but without destroying all child components
            commit("setComponentCountKey", state.component_count_key + 1);
        },
        setLastSearchedItems({ commit }, last_searched_items) {
            commit("setLastSearchedItems", last_searched_items);
        },
        setSelectedLocationItemComponentUuids({ commit }, selected_location_item_component_uuids) {
            commit("setSelectedLocationItemComponentUuids", selected_location_item_component_uuids);
        },
        setSelectionMode({ commit }, selection_mode) {
            commit("setSelectionMode", selection_mode);
        },
        setInspectionFilters({ commit, state }, { inspection_id, filters }) {
            commit("setInspectionFilters", { inspection_id, filters });
        },
        
        // Set individual filter with inspection context
        setInInspection({ commit, state, getters }, in_inspection) {
            const inspection_id = getters.getInspectionId;
            if (!inspection_id) return;
            
            const currentFilters = getters.getInspectionFilters(inspection_id) || { in_inspection: false, flat_view: false, selected_component_types: [] };
            const newFilters = { ...currentFilters, in_inspection };
            
            commit("setInspectionFilters", { inspection_id, filters: newFilters });
        },
        
        // Set flat view with inspection context
        setFlatView({ commit, state, getters }, flat_view) {
            const inspection_id = getters.getInspectionId;
            if (!inspection_id) return;
            
            const currentFilters = getters.getInspectionFilters(inspection_id) || { in_inspection: false, flat_view: false, selected_component_types: [] };
            const newFilters = { ...currentFilters, flat_view };
            
            commit("setInspectionFilters", { inspection_id, filters: newFilters });
        },
        setShowSidebar({ commit }, show_sidebar) {
            commit("setShowSidebar", show_sidebar);
        },
        setLastSyncInspections({ commit }, last_sync_inspections) {
            commit("setLastSyncInspections", last_sync_inspections);
        },
        setInspectionTab({ commit }, tab_inspection) {
            commit("setInspectionTab", tab_inspection);
        },
        setComponentTab({ commit }, tab_component) {
            commit("setComponentTab", tab_component);
        },
        setUpdateModal({ commit }, update_modal) {
            commit("setUpdateModal", update_modal);
        },
        async checkForUpdates({ commit, state }, { force = false } = {}) {
            try {
                console.log('🏪 [Store] checkForUpdates called with force=', force);
                console.log('🏪 [Store] Current updateInfo in state:', JSON.stringify(state.updateInfo));

                let updateInfo;
                if (force) {
                    // Force immediate check (bypass 24-hour cache)
                    console.log('🏪 [Store] Forcing update check (bypassing cache)');
                    updateInfo = await updateService.checkForUpdates();
                } else {
                    // Normal daily check (respects 24-hour cache)
                    console.log('🏪 [Store] Daily update check (respects cache)');
                    updateInfo = await updateService.dailyUpdateCheck();
                }

                console.log('🏪 [Store] updateService returned:', JSON.stringify(updateInfo));

                if (updateInfo) {
                    console.log('🏪 [Store] Clearing updateInfo first...');
                    // Clear first to force watcher to fire even if value is the same
                    commit("setUpdateInfo", null);
                    console.log('🏪 [Store] updateInfo cleared, now setting new value...');
                    // Then set the new value
                    commit("setUpdateInfo", updateInfo);
                    console.log('🏪 [Store] updateInfo committed:', JSON.stringify(updateInfo));
                    return updateInfo;
                }
                console.log('🏪 [Store] No updateInfo, returning null');
                return null;
            } catch (error) {
                console.error("🏪 [Store] Error checking for updates:", error);
                return null;
            }
        },
        clearUpdateInfo({ commit }) {
            commit("setUpdateInfo", null);
        },
        setUpdateModalOpen({ commit }, isOpen) {
            commit("setUpdateModalOpen", isOpen);
        },
        setAppActive({ commit }, isActive) {
            commit("setAppActive", isActive);
        },
        initializeListeners({ commit, state }) {
            // first we check if listener is already initialized
            // if it is, we remove it
            Network.removeAllListeners();

            Network.addListener("networkStatusChange", (status) => {
                // Simple direct approach - update state and let watcher handle queue
                if (!state.force_offline) {
                    commit("setOnline", status.connected);
                    commit("setConnectionType", status.connectionType);
                } else {
                    commit("setOnline", false);
                }
            });

            App.removeAllListeners();

            App.addListener("appStateChange", async ({ isActive }) => {
                console.log("🔥 APP STATE CHANGE EVENT:", isActive ? "ACTIVE" : "BACKGROUND");

                commit("setAppIsActive", isActive);
                commit("setAppActive", isActive); // Also update the appActive state for Camera.vue

                // When app becomes active, force network check
                if (isActive) {
                    console.log("App became active, forcing network status check");

                    // Check for app updates when app becomes active
                    // Note: This always uses novacheck.nl, no login required
                    console.log("✅ App became active - checking for updates...");
                    try {
                        console.log("📱 Calling updateService.dailyUpdateCheck()...");
                        const updateInfo = await updateService.dailyUpdateCheck();
                        console.log("📱 Update check returned:", updateInfo);

                        if (updateInfo) {
                            console.log("📱 Setting update info in store:", updateInfo);
                            // Clear first to force watcher to fire even if value is the same
                            commit("setUpdateInfo", null);
                            // Then set the new value
                            commit("setUpdateInfo", updateInfo);
                            console.log("📱 Update info committed to store");
                        } else {
                            console.log("📱 No update info returned (check not needed or no update)");
                        }
                    } catch (error) {
                        console.error("❌ Error checking for updates on app state change:", error);
                        console.error("❌ Error stack:", error.stack);
                    }

                    try {
                        // Get fresh network status
                        const networkStatus = await Network.getStatus();
                        console.log(`Direct network check from app state: ${JSON.stringify(networkStatus)}`);

                        // If we're not in force offline mode, update network state
                        if (!state.force_offline) {
                            commit("setOnline", networkStatus.connected);
                            commit("setConnectionType", networkStatus.connectionType);

                            // If we're online and were previously offline,
                            // This will trigger the online watcher in App.vue
                            if (networkStatus.connected && !state.online) {
                                console.log("App became active, detected online state change");
                                // Force immediate check for jobs after slight delay
                                // Always process queue when app becomes active, regardless of job count
                                setTimeout(async () => {
                                    // Don't check for jobs - always process unconditionally
                                    console.log(
                                        "App activation in store - starting queue with aggressive force params"
                                    );
                                    try {
                                        // Use the exact same approach as the online watcher in App.vue
                                        await syncApi.processQueueWithParams({
                                            ignoreOffline: true,
                                            forceSync: true,
                                        });
                                    } catch (err) {
                                        console.log("Error in store app activation queue processing:", err);

                                        // If first attempt fails, try all fallbacks - exactly like App.vue
                                        setTimeout(async () => {
                                            console.log("Trying all fallbacks after store app activation");
                                            // Force worker reset first
                                            syncApi.stopAll();

                                            // Wait a moment then retry with emergency mode
                                            setTimeout(async () => {
                                                console.log("Emergency retry for store app activation");
                                                try {
                                                    await syncApi.processQueueWithParams({
                                                        ignoreOffline: true,
                                                        iosControlCenter: true,
                                                        forceSync: true,
                                                        emergencyMode: true,
                                                    });
                                                } catch (finalErr) {
                                                    // Last resort - just try basic queue
                                                    syncApi.processQueue();
                                                }
                                            }, 500);
                                        }, 1000);
                                    }
                                }, 500);
                            }
                        }
                    } catch (error) {
                        console.error("Error checking network on app state change:", error);
                    }
                }
            });
        },
    },
    getters: {
        getSetup(state) {
            return state.setup;
        },
        getForceOffline(state) {
            return state.force_offline;
        },
        getInspectionSyncProgress(state) {
            return state.inspection_sync_progress;
        },
        getQueueProgress(state) {
            return state.queue_progress;
        },
        getQueue(state) {
            return state.queue;
        },
        getSyncing(state) {
            return state.syncing;
        },
        getSpeeds(state) {
            return state.speeds;
        },
        getTempSave(state) {
            return state.temp_save;
        },
        getImageTimestamp(state) {
            return state.image_timestamp;
        },
        getImageCoordinates(state) {
            return state.image_coordinates;
        },
        getImageSaveInGallery(state) {
            return state.image_save_in_gallery;
        },
        getImageSaveLocalBackup(state) {
            return state.image_save_local_backup;
        },
        getImageInspectorName(state) {
            return state.image_set_inspector_name;
        },
        getFlash(state) {
            return state.flash;
        },
        getMeasurmentTypeOpened(state) {
            return state.measurment_type_opened;
        },
        getOrientation(state) {
            return state.orientation;
        },
        getInspectionMethodCode(state) {
            return state.inspection_method_code;
        },
        getShowInstruments(state) {
            return state.show_instruments;
        },
        getTorch(state) {
            return state.torch;
        },
        getSaveImageLocally(state) {
            return state.save_image_locally;
        },
        getLineThickness(state) {
            return state.line_thickness;
        },
        getServerStatus(state) {
            return state.server_status;
        },
        getEcho(state) {
            return state.echo;
        },
        getEventNotification(state) {
            return state.event_notification;
        },
        getGlobalSettings(state) {
            return state.global_settings;
        },
        getTours(state) {
            return state.tours;
        },
        getToursCompleted(state) {
            return state.tours_completed;
        },
        getTourCompleted: (state) => (tour) => {
            return state.tours_completed.includes(tour);
        },
        getBearer(state) {
            return state.bearer;
        },
        getApiUrl(state) {
            return state.api_url;
        },
        getActiveInspection(state) {
            return state.active_inspection;
        },
        getLocationItem: (state) => {
            return state.location_item;
        },
        online: (state) => {
            return state.force_offline ? false : state.online;
        },
        getDarkMode: (state) => {
            return state.dark_mode;
        },
        getUser: (state) => {
            return state.user;
        },
        loading: (state) => {
            return state.loading;
        },
        getLocationItemState: (state) => (uuid) => {
            let this_state = state.location_item_states.find((item) => item.uuid == uuid);
            if (this_state) {
                return this_state;
            }
        },
        getLocationItemComponentState: (state) => (uuid) => {
            let this_state = state.location_item_component_states.find((item) => item.uuid == uuid);
            if (this_state) {
                return this_state;
            }
        },
        getInspectionId: (state) => {
            return state.inspection_id;
        },
        getLocationItemUuid: (state) => {
            return state.location_item_uuid;
        },
        getLocationItemComponentUuid: (state) => {
            return state.location_item_component_uuid;
        },
        getTreeFilters: (state) => {
            return state.tree_filters;
        },
        getDefaultMeasurementInstruments: (state) => {
            return state.default_measurement_instruments;
        },
        getRemarksOpened: (state) => {
            return state.remarks_opened;
        },
        isAuthenticated: () => {
            return localStorage.getItem("bearer") != null;
        },
        getInspectionPolling(state) {
            return state.inspection_polling;
        },
        getSelectedLocationItem(state) {
            return state.selected_location_item_uuid;
        },
        getChecklistAnswers(state) {
            return state.checklist_answers;
        },
        getListLoading(state) {
            return state.list_loading;
        },
        getLocationId(state) {
            return state.location_id;
        },
        getActionPackage(state) {
            return state.action_package;
        },
        getNotificationIds(state) {
            return state.notification_ids;
        },
        getActiveMenu(state) {
            return state.active_menu;
        },
        getActionsFilter(state) {
            return state.actions_filter;
        },
        getActiveAddress(state) {
            return state.active_address;
        },
        getDragMode(state) {
            return state.drag_mode;
        },
        getLocationUuid(state) {
            return state.location_uuid;
        },
        getInspectionUuid(state) {
            return state.inspection_uuid;
        },
        getScrollHeight(state) {
            return state.scroll_height;
        },
        getComponentCountKey(state) {
            return state.component_count_key;
        },
        getLastSearchedItems(state) {
            return state.last_searched_items;
        },
        getSelectedLocationItemComponentUuids(state) {
            return state.selected_location_item_component_uuids;
        },
        getSelectionMode(state) {
            return state.selection_mode;
        },
        // Get all filters for a specific inspection
        getInspectionFilters: (state) => (inspection_id) => {
            return state.inspection_filters[inspection_id] || { in_inspection: false, flat_view: false, selected_component_types: [] };
        },
        
        // Get the in_inspection filter value for current inspection
        getInInspection(state, getters) {
            const inspection_id = getters.getInspectionId;
            if (!inspection_id) return false;
            
            const filters = getters.getInspectionFilters(inspection_id);
            return filters.in_inspection || false;
        },
        
        // Get the flat_view filter value for current inspection
        getFlatView(state, getters) {
            const inspection_id = getters.getInspectionId;
            if (!inspection_id) return false;
            
            const filters = getters.getInspectionFilters(inspection_id);
            return filters.flat_view || false;
        },
        
        // Get the selected_component_types filter value for current inspection
        getSelectedComponentTypes(state, getters) {
            const inspection_id = getters.getInspectionId;
            if (!inspection_id) return [];
            
            const filters = getters.getInspectionFilters(inspection_id);
            const selectedTypes = filters.selected_component_types || [];
            console.log('Store getter - Selected component types for inspection', inspection_id, ':', selectedTypes);
            return selectedTypes;
        },
        getShowSidebar(state) {
            return state.show_sidebar;
        },
        getLastSyncInspections(state) {
            return state.last_sync_inspections;
        },
        getInspectionTab(state) {
            return state.tab_inspection;
        },
        getComponentTab(state) {
            return state.tab_component;
        },
        getUpdateModal(state) {
            return state.update_modal;
        },
        getUpdateInfo(state) {
            return state.updateInfo;
        },
        getUpdateModalOpen(state) {
            return state.updateModalOpen;
        },
        getConnectionType(state) {
            return state.connectionType;
        },
        getSyncWifiOnly(state) {
            return state.sync_wifi_only;
        },
        checkSetting: (state) => (setting) => {
            // we check state.global_settings for the setting
            // if it exists, and it setting.pivot.value is "true", we return true
            // otherwise we return false

            let setting_value = state.global_settings.find((item) => item.code == setting);
            if (setting_value) {
                return setting_value.pivot.value == "true";
            } else {
                return false;
            }
        },
        isActive: (state) => {
            return state.is_active;
        },
        getInspectionSynced: (state) => (inspection_uuid) => {
            let inspection_synced = state.inspections_synced.find((item) => item.inspection_uuid == inspection_uuid);

            if (inspection_synced) {
                return inspection_synced.updated_at;
            } else {
                return null;
            }
        },
        appActive: (state) => {
            return state.appActive;
        },
    },
    plugins: [new VuexPersistence().plugin],
});
